import { ApolloError, ApolloQueryResult } from '@apollo/client';
import { useEffect, useState } from 'react';
import * as Rx from 'rxjs';
import { delay, filter, flatMap, map, switchMap, tap } from 'rxjs/operators';

import { client } from '../../../../async/utils/client';
import { SearchCollaboratorsQuery, SearchCollaboratorsQueryVariables, SearchUser } from '../../../../graphql/gen/types';
import { SEARCH_COLLABORATORS } from '../../../../graphql/queries/SearchCollaborators';

type QueryStreamState = Rx.Subject<QueryState>;

const SEARCH_DEBOUNCE_TIME_MS = 300;
type Option = SearchUser;
export type QueryState = string | null;
export type UserSearchState =
  | { state: 'initial' }
  | { state: 'more_input_needed' }
  | { state: 'can_load' }
  | { state: 'loading' }
  | { state: 'done'; options: Option[] }
  | { state: 'error'; error: string };

type Return = {
  query: string | null;
  setQuery: (query: string | null) => void;
  search: UserSearchState;
};

export const usePlexUserSearch = ({
  onlySignedUpUsers,
  minimumQueryLength,
}: {
  onlySignedUpUsers: boolean;
  minimumQueryLength: number;
}): Return => {
  const [query, setQuery] = useState<QueryState>(null);
  const [search, setSearch] = useState<UserSearchState>({ state: 'initial' });
  const [queryStream] = useState<QueryStreamState>(new Rx.BehaviorSubject<QueryState>(null));

  /*
  Handle `queryStream` events, computing search state, debouncing, triggering queries to
  the server and setting the response state
  */
  useEffect(() => {
    const subscription = queryStream
      .pipe(
        // Use `switchMap` to handle only the latest `query` and cancel handling of previous queries
        switchMap((query: string | null) =>
          Rx.from([query]).pipe(
            // Compute equivalent search state
            map((query: string | null): { query: string | null; search: UserSearchState } => {
              if (query === null) {
                return { query, search: { state: 'initial' } };
              } else if (query.length < minimumQueryLength) {
                return { query, search: { state: 'more_input_needed' } };
              } else {
                return { query, search: { state: 'can_load' } };
              }
            }),
            // Synchronize with React
            tap(({ search }) => {
              setSearch(search);
            }),
            // Filter only events which could trigger a load
            filter(({ search }) => search.state === 'can_load'),
            // Delay events to simulate debounce behavior.
            // If newer `query` is emitted from `queryStream`, this will not emit further due to `switchMap`
            delay(SEARCH_DEBOUNCE_TIME_MS),
            // Synchronize with React
            tap(() => {
              setSearch({ state: 'loading' });
            }),
            // Trigger request to server
            flatMap(({ query }) =>
              Rx.from(
                client.query<SearchCollaboratorsQuery, SearchCollaboratorsQueryVariables>({
                  query: SEARCH_COLLABORATORS,
                  variables: { query, onlySignedUpUsers },
                })
              )
            ),
            // Synchronize response with React
            tap(
              (result: ApolloQueryResult<SearchCollaboratorsQuery>) => {
                setSearch({
                  state: 'done',
                  options: result.data?.loggedIn?.searchUsers?.results || [],
                });
              },
              (error: ApolloError) => {
                setSearch({
                  state: 'error',
                  error: error.message,
                });
              }
            )
          )
        )
      )
      // Start execution of this observable
      .subscribe();

    const cleanupEffect = (): void => {
      // Cancel execution of this observable
      subscription.unsubscribe();
      queryStream.complete();
    };
    return cleanupEffect;
  }, [minimumQueryLength, onlySignedUpUsers, queryStream]);

  /*
  Publish updates to `query` to the subscriber of `queryStream`
  */
  useEffect(() => {
    queryStream.next(query);
  }, [query, queryStream]);

  return { query, setQuery, search };
};
